/**
 * The typed write-door client (docs/write-door-api.md).
 *
 * Same-origin `/api/*` only: the page sits behind Cloudflare Access and the
 * browser attaches the Access cookie itself — the board embeds no token
 * (write-door §2). A 401 on poll means the Access session expired; the caller
 * reloads into the OTP flow.
 *
 * In `npm run dev` these requests are answered by the Vite mock middleware
 * (mock/write-door.ts), never by a real Worker.
 */

import type {
  BoardState,
  CheckRequest,
  CheckResponse,
  ComposeRequest,
  ComposeResponse,
  ModeRequest,
  ModeResponse,
} from './types';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail?: string;
  constructor(status: number, code: string, detail?: string) {
    super(detail ? `${code}: ${detail}` : code);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
  /** Access session expired — the board must reload into the OTP flow. */
  get isAuthExpired(): boolean {
    return this.status === 401;
  }
}

export function uuid(): string {
  const c = globalThis.crypto;
  if (c && 'randomUUID' in c) return c.randomUUID();
  // Non-secure fallback for older browsers; the id is only an idempotency key.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** Extra query params the dev mock honours (scenario switching); empty in prod. */
export type QueryExtras = Record<string, string>;

export class BoardApi {
  private readonly base: string;
  private readonly extras: QueryExtras;

  constructor(base = '/api', extras: QueryExtras = {}) {
    this.base = base;
    this.extras = extras;
  }

  private url(path: string, params: Record<string, string | undefined> = {}): string {
    const u = new URL(this.base + path, location.origin);
    for (const [k, v] of Object.entries({ ...this.extras, ...params })) {
      if (v !== undefined) u.searchParams.set(k, v);
    }
    return u.pathname + u.search;
  }

  private async request<T>(path: string, init?: RequestInit, params?: Record<string, string>): Promise<T> {
    let res: Response;
    try {
      res = await fetch(this.url(path, params), {
        credentials: 'same-origin',
        ...init,
        headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
      });
    } catch (e) {
      // Worker unreachable: the caller keeps its last render + stale banner.
      throw new ApiError(0, 'unreachable', e instanceof Error ? e.message : String(e));
    }
    const text = await res.text();
    const body: unknown = text ? safeJson(text) : null;
    if (!res.ok) {
      const b = body as { error?: string; detail?: string } | null;
      throw new ApiError(res.status, b?.error ?? `http_${res.status}`, b?.detail);
    }
    return body as T;
  }

  /** The one polled read endpoint (write-door §4). */
  boardState(refresh = false): Promise<BoardState> {
    return this.request<BoardState>('/board-state', { method: 'GET' }, refresh ? { refresh: '1' } : {});
  }

  /** Every checkbox on the board (write-door §5.1). */
  check(req: CheckRequest): Promise<CheckResponse> {
    return this.request<CheckResponse>('/check', { method: 'POST', body: JSON.stringify(req) });
  }

  /** The morning Day-plan compose — the daily review's stamp (write-door §5.2). */
  compose(req: ComposeRequest): Promise<ComposeResponse> {
    return this.request<ComposeResponse>('/compose', { method: 'POST', body: JSON.stringify(req) });
  }

  /** The Mode toggle (write-door §5.3). */
  setMode(req: ModeRequest): Promise<ModeResponse> {
    return this.request<ModeResponse>('/mode', { method: 'POST', body: JSON.stringify(req) });
  }

  /** Sent alongside the poll while Mode is on. */
  heartbeat(): Promise<{ isOn: boolean; recorded: boolean }> {
    return this.request('/mode/heartbeat', {
      method: 'POST',
      body: JSON.stringify({ clientUuid: uuid() }),
    });
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
